/**
 * Os módulos que o lojista liga e desliga, do lado da vitrine.
 *
 * Location: src/lib
 * Why: a loja recebe **booleanos** no payload que já busca no boot (o painel é
 * quem vê os três estados). Aqui a pergunta é uma só: mostro isto?
 *
 * O padrão quando o campo não vem é **ligado**. Uma API mais antiga, ou uma
 * resposta em cache de antes desta mudança, não tem `addons` — e esconder o
 * fórum inteiro por causa de um campo ausente seria trocar um recurso
 * funcionando por uma loja quebrada. Quem barra de verdade é o guard da API:
 * se o módulo estiver mesmo desligado, a página cai sozinha.
 */

export const ADDONS = ['forum', 'wiki', 'support', 'forms', 'launcher', 'discord', 'vip'] as const

export type Addon = (typeof ADDONS)[number]

export type AddonsPublicos = Partial<Record<Addon, boolean>>

/**
 * De rota para módulo.
 *
 * Prefixo, não caminho exato: `/forum/geral/regras/123` pertence ao fórum
 * tanto quanto `/forum`. Sem isso, desligar o módulo esconderia o índice e
 * deixaria cada tópico acessível pelo link que já circulou no Discord.
 */
export const ADDON_POR_PREFIXO: Array<[string, Addon]> = [
  ['/forum', 'forum'],
  ['/wiki', 'wiki'],
  ['/suporte', 'support'],
  ['/formularios', 'forms'],
  ['/minhas-respostas', 'forms'],
  ['/download', 'launcher'],
  ['/changelog', 'launcher'],
]

export function addonAtivo(addons: AddonsPublicos | undefined, addon: Addon): boolean {
  return addons?.[addon] !== false
}

/** O módulo dono desta rota, ou `null` se a rota não pertence a nenhum. */
export function addonDaRota(pathname: string): Addon | null {
  const encontrado = ADDON_POR_PREFIXO.find(
    ([prefixo]) => pathname === prefixo || pathname.startsWith(`${prefixo}/`),
  )

  return encontrado ? encontrado[1] : null
}

/** A rota está visível com estes addons? Rota sem dono está sempre visível. */
export function rotaVisivel(addons: AddonsPublicos | undefined, pathname: string): boolean {
  const addon = addonDaRota(pathname)

  return addon === null || addonAtivo(addons, addon)
}
